import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatCurrency, formatDateBR, todayISO } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Fuel, Download, Printer, Copy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const COMBUSTIVEIS = [
    { value: "diesel", label: "Diesel Comum (S500)" },
    { value: "diesel_s10", label: "Diesel S10" },
    { value: "gasolina", label: "Gasolina Comum" },
    { value: "etanol", label: "Etanol" },
    { value: "gnv", label: "GNV" },
    { value: "outro", label: "Outro" },
];
const COMB_LABELS = Object.fromEntries(COMBUSTIVEIS.map((c) => [c.value, c.label]));

const EMPREENDIMENTOS = [
    { value: "km117", label: "Empreendimento KM 117" },
    { value: "rio_cupari", label: "Rio Cupari" },
    { value: "sede", label: "Sede COOMFLONA" },
    { value: "setor_social", label: "Setor Social" },
    { value: "nao_madeireiro", label: "Não Madeireiro" },
    { value: "outro", label: "Outro" },
];
const EMPR_LABELS = Object.fromEntries(EMPREENDIMENTOS.map((e) => [e.value, e.label]));

const empty = {
    data: todayISO(), posto: "", tipo_combustivel: "diesel_s10",
    litros: "", valor_litro: "", valor_total: "",
    veiculo_placa: "", veiculo_modelo: "", motorista_nome: "",
    empreendimento: "sede", numero_nota: "", km_atual: null, observacoes: "",
};

export default function FuelReceipts() {
    // eslint-disable-next-line no-unused-vars
    const { isAdmin } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [mesFilter, setMesFilter] = useState("");
    const [emprFilter, setEmprFilter] = useState("todos");

    const load = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (mesFilter) params.mes = mesFilter;
        if (emprFilter !== "todos") params.empreendimento = emprFilter;
        const res = await api.get("/fuel-receipts", { params });
        setItems(res.data || []);
        setLoading(false);
    }, [mesFilter, emprFilter]);

    useEffect(() => { load(); }, [load]);

    const totals = useMemo(() => {
        const total = items.reduce((a, i) => a + (i.valor_total || 0), 0);
        const litros = items.reduce((a, i) => a + (i.litros || 0), 0);
        const porEmpr = {};
        items.forEach((i) => {
            porEmpr[i.empreendimento] = (porEmpr[i.empreendimento] || 0) + (i.valor_total || 0);
        });
        return { total, litros, porEmpr };
    }, [items]);

    // Auto-calcular valor_total quando litros × valor_litro
    const updateLitros = (v) => {
        const l = parseFloat(v) || 0;
        const vl = parseFloat(form.valor_litro) || 0;
        setForm({ ...form, litros: v, valor_total: vl > 0 ? Number((l * vl).toFixed(2)) : form.valor_total });
    };
    const updateValorLitro = (v) => {
        const vl = parseFloat(v) || 0;
        const l = parseFloat(form.litros) || 0;
        setForm({ ...form, valor_litro: v, valor_total: l > 0 ? Number((l * vl).toFixed(2)) : form.valor_total });
    };

    const openNew = () => { setForm(empty); setEditingId(null); setOpen(true); };
    const openEdit = (i) => {
        setForm({
            data: i.data, posto: i.posto, tipo_combustivel: i.tipo_combustivel,
            litros: i.litros ?? "", valor_litro: i.valor_litro ?? "", valor_total: i.valor_total,
            veiculo_placa: i.veiculo_placa, veiculo_modelo: i.veiculo_modelo || "",
            motorista_nome: i.motorista_nome || "",
            empreendimento: i.empreendimento, numero_nota: i.numero_nota || "",
            km_atual: i.km_atual, observacoes: i.observacoes || "",
        });
        setEditingId(i.id);
        setOpen(true);
    };

    // Duplicar: abre um novo formulário pré-preenchido com dados da nota selecionada
    // (troca a data para hoje e limpa o Nº NF e valor, agilizando lançamento em série)
    const duplicate = (i) => {
        setForm({
            data: todayISO(), posto: i.posto, tipo_combustivel: i.tipo_combustivel,
            litros: "", valor_litro: i.valor_litro ?? "", valor_total: "",
            veiculo_placa: i.veiculo_placa, veiculo_modelo: i.veiculo_modelo || "",
            motorista_nome: i.motorista_nome || "",
            empreendimento: i.empreendimento, numero_nota: "",
            km_atual: null, observacoes: "",
        });
        setEditingId(null);
        setOpen(true);
        toast.info(`Copiado de ${i.posto} — preencha litros e valor`);
    };

    const save = async () => {
        if (!form.posto.trim() || !form.veiculo_placa.trim() || !form.data) {
            toast.error("Preencha posto, placa e data");
            return;
        }
        const payload = {
            ...form,
            litros: parseFloat(form.litros) || 0,
            valor_litro: parseFloat(form.valor_litro) || 0,
            valor_total: parseFloat(form.valor_total) || 0,
            km_atual: form.km_atual === null || form.km_atual === "" ? null : parseFloat(form.km_atual),
        };
        try {
            if (editingId) {
                await api.put(`/fuel-receipts/${editingId}`, payload);
                toast.success("Nota atualizada!");
            } else {
                await api.post("/fuel-receipts", payload);
                toast.success("Nota registrada!");
            }
            setOpen(false);
            load();
        } catch {
            toast.error("Erro ao salvar");
        }
    };

    const del = async (id) => {
        try {
            await api.delete(`/fuel-receipts/${id}`);
            toast.success("Removida");
            load();
        } catch {
            toast.error("Erro ao remover");
        } finally {
            setConfirmDelete(null);
        }
    };

    const exportCsv = async () => {
        try {
            const params = {};
            if (mesFilter) params.mes = mesFilter;
            if (emprFilter !== "todos") params.empreendimento = emprFilter;
            const res = await api.get("/fuel-receipts/export", { params, responseType: "blob" });
            const filename = `combustivel_${emprFilter}_${mesFilter || "completo"}.csv`;
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch {
            toast.error("Erro ao exportar");
        }
    };

    return (
        <div className="space-y-6" data-testid="fuel-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-3xl font-extrabold text-foreground flex items-center gap-3">
                        <Fuel className="w-7 h-7 text-primary" /> Notas de Combustível
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">Controle de abastecimentos e notas fiscais</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={exportCsv} className="gap-2" data-testid="btn-export-fuel">
                        <Download className="w-4 h-4" /> Exportar CSV
                    </Button>
                    <Button onClick={openNew} className="gap-2" data-testid="btn-new-fuel">
                        <Plus className="w-4 h-4" /> Nova Nota
                    </Button>
                </div>
            </div>

            <Card>
                <CardContent className="p-4 flex items-end gap-3 flex-wrap">
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1">Mês</label>
                        <Input type="month" value={mesFilter} onChange={(e) => setMesFilter(e.target.value)} className="w-44" data-testid="input-fuel-mes" />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-1">Empreendimento</label>
                        <Select value={emprFilter} onValueChange={setEmprFilter}>
                            <SelectTrigger className="w-56" data-testid="select-fuel-empr"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="todos">Todos</SelectItem>
                                {EMPREENDIMENTOS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {(mesFilter || emprFilter !== "todos") && (
                        <Button variant="ghost" size="sm" onClick={() => { setMesFilter(""); setEmprFilter("todos"); }}>Limpar</Button>
                    )}
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card><CardContent className="p-5">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Total geral</div>
                    <div className="mt-2 text-2xl font-extrabold text-primary">{formatCurrency(totals.total)}</div>
                    <div className="text-xs text-muted-foreground mt-1">{items.length} nota(s)</div>
                </CardContent></Card>
                <Card><CardContent className="p-5">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Litros abastecidos</div>
                    <div className="mt-2 text-2xl font-extrabold">{totals.litros.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} L</div>
                </CardContent></Card>
                <Card><CardContent className="p-5">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Por empreendimento</div>
                    <div className="mt-1 space-y-0.5 text-xs">
                        {Object.entries(totals.porEmpr).map(([k, v]) => (
                            <div key={k} className="flex justify-between"><span>{EMPR_LABELS[k]}</span><strong>{formatCurrency(v)}</strong></div>
                        ))}
                        {Object.keys(totals.porEmpr).length === 0 && <span className="text-muted-foreground">—</span>}
                    </div>
                </CardContent></Card>
            </div>

            <Card>
                <CardContent className="p-5">
                    {loading ? (
                        <div className="text-center py-8 text-muted-foreground">Carregando...</div>
                    ) : items.length === 0 ? (
                        <div className="py-14 text-center border-2 border-dashed border-border rounded-xl">
                            <Fuel className="w-10 h-10 mx-auto text-muted-foreground" />
                            <div className="mt-3 font-semibold">Nenhuma nota cadastrada</div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="fuel-table">
                                <thead>
                                    <tr className="text-left text-muted-foreground border-b border-border">
                                        <th className="py-2 px-2 font-semibold">Data</th>
                                        <th className="py-2 px-2 font-semibold">Posto</th>
                                        <th className="py-2 px-2 font-semibold">Combustível</th>
                                        <th className="py-2 px-2 font-semibold">Placa</th>
                                        <th className="py-2 px-2 font-semibold">Empreendimento</th>
                                        <th className="py-2 px-2 font-semibold text-right">Litros</th>
                                        <th className="py-2 px-2 font-semibold text-right">Valor</th>
                                        <th className="py-2 px-2 text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((i) => (
                                        <tr key={i.id} className="border-b border-border row-hover">
                                            <td className="py-2 px-2">{formatDateBR(i.data)}</td>
                                            <td className="py-2 px-2 font-medium">{i.posto}</td>
                                            <td className="py-2 px-2 text-xs"><Badge variant="outline">{COMB_LABELS[i.tipo_combustivel]}</Badge></td>
                                            <td className="py-2 px-2 font-mono">{i.veiculo_placa}</td>
                                            <td className="py-2 px-2 text-xs">{EMPR_LABELS[i.empreendimento]}</td>
                                            <td className="py-2 px-2 text-right">{(i.litros || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} L</td>
                                            <td className="py-2 px-2 text-right font-semibold">{formatCurrency(i.valor_total)}</td>
                                            <td className="py-2 px-2 text-right">
                                                <div className="flex gap-1 justify-end">
                                                    <Button variant="ghost" size="sm" onClick={() => duplicate(i)} title="Duplicar (lançamento rápido)" data-testid={`btn-dup-fuel-${i.id}`}>
                                                        <Copy className="w-4 h-4 text-emerald-700" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => window.open(`/combustivel/${i.id}/imprimir`, "_blank")} title="Imprimir" data-testid={`btn-print-fuel-${i.id}`}>
                                                        <Printer className="w-4 h-4 text-primary" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => openEdit(i)} title="Editar" data-testid={`btn-edit-fuel-${i.id}`}>
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(i)} title="Excluir" data-testid={`btn-delete-fuel-${i.id}`}>
                                                        <Trash2 className="w-4 h-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Editar" : "Nova"} Nota de Combustível</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label>Data *</Label>
                                <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="input-fuel-data" />
                            </div>
                            <div>
                                <Label>Combustível *</Label>
                                <Select value={form.tipo_combustivel} onValueChange={(v) => setForm({ ...form, tipo_combustivel: v })}>
                                    <SelectTrigger data-testid="select-fuel-tipo"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {COMBUSTIVEIS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Empreendimento *</Label>
                                <Select value={form.empreendimento} onValueChange={(v) => setForm({ ...form, empreendimento: v })}>
                                    <SelectTrigger data-testid="select-fuel-empr-form"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {EMPREENDIMENTOS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Nome do posto *</Label>
                            <Input value={form.posto} onChange={(e) => setForm({ ...form, posto: e.target.value })} placeholder="Ex: Posto XYZ" data-testid="input-fuel-posto" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label>Litros</Label>
                                <Input type="number" step="0.01" value={form.litros} onChange={(e) => updateLitros(e.target.value)} placeholder="0,00" data-testid="input-fuel-litros" />
                            </div>
                            <div>
                                <Label>Valor por litro (R$)</Label>
                                <Input type="number" step="0.001" value={form.valor_litro} onChange={(e) => updateValorLitro(e.target.value)} placeholder="0,000" data-testid="input-fuel-valor-l" />
                            </div>
                            <div>
                                <Label>Valor total (R$) *</Label>
                                <Input type="number" step="0.01" value={form.valor_total} onChange={(e) => setForm({ ...form, valor_total: e.target.value })} placeholder="0,00" data-testid="input-fuel-total" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Placa do veículo *</Label>
                                <Input value={form.veiculo_placa} onChange={(e) => setForm({ ...form, veiculo_placa: e.target.value.toUpperCase() })} placeholder="ABC-1234" data-testid="input-fuel-placa" />
                            </div>
                            <div>
                                <Label>Modelo do veículo</Label>
                                <Input value={form.veiculo_modelo} onChange={(e) => setForm({ ...form, veiculo_modelo: e.target.value })} data-testid="input-fuel-modelo" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Motorista</Label>
                                <Input value={form.motorista_nome} onChange={(e) => setForm({ ...form, motorista_nome: e.target.value })} data-testid="input-fuel-motorista" />
                            </div>
                            <div>
                                <Label>Nº da nota fiscal</Label>
                                <Input value={form.numero_nota} onChange={(e) => setForm({ ...form, numero_nota: e.target.value })} placeholder="Ex: 12345" data-testid="input-fuel-nf" />
                            </div>
                        </div>
                        <div>
                            <Label>KM atual do veículo (opcional)</Label>
                            <Input type="number" value={form.km_atual || ""} onChange={(e) => setForm({ ...form, km_atual: e.target.value })} placeholder="Ex: 45230" data-testid="input-fuel-km" />
                        </div>
                        <div>
                            <Label>Observações</Label>
                            <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="input-fuel-obs" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} data-testid="btn-save-fuel">Salvar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover nota?</AlertDialogTitle>
                        <AlertDialogDescription>{confirmDelete?.posto} — {formatDateBR(confirmDelete?.data)}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(confirmDelete.id)}>Remover</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
